import React, { useState, useMemo, useEffect } from 'react';
import DateRangePicker from '../finance/components/DateRangePicker';
import { getDatePresetLabel } from '../finance/data/mockData';
import { fmtMoney } from './productCommerce';
import { productService } from '../../_services/product.service';
import { HeroInsight, Leaderboard, OpportunityList, StatTiles, BarList, SegmentSplit } from '../catalogue-data';
import '../gift-cards-v2/GiftCardsV2.css'; // .v2-sales-filterbar chrome for the date bar
import './ProductV2.css';

// Products > Data — retail intelligence (who's selling, retail on top of
// appointments, where the next pound is). Real data from /product/reporting
// (POS booking.products + online orders). Money ledger lives in Finance (link top-right).

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 9l6 6 6-6" /></svg>
);
const ArrowRightIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);

const computePresetRange = (preset) => {
  const now = new Date();
  let startDate, endDate;
  switch (preset) {
    case 'today':       startDate = endDate = new Date(); break;
    case 'yesterday': { const d = new Date(); d.setDate(d.getDate() - 1); startDate = endDate = d; break; }
    case 'last7':       endDate = new Date(); startDate = new Date(); startDate.setDate(startDate.getDate() - 7); break;
    case 'last4weeks':  endDate = new Date(); startDate = new Date(); startDate.setDate(startDate.getDate() - 28); break;
    case 'last6months': endDate = new Date(); startDate = new Date(); startDate.setMonth(startDate.getMonth() - 6); break;
    case 'last12months':endDate = new Date(); startDate = new Date(); startDate.setFullYear(startDate.getFullYear() - 1); break;
    case 'mtd':         endDate = new Date(); startDate = new Date(now.getFullYear(), now.getMonth(), 1); break;
    case 'ytd':         endDate = new Date(); startDate = new Date(now.getFullYear(), 0, 1); break;
    case 'all':         endDate = new Date(); startDate = new Date(2020, 0, 1); break;
    default:            endDate = new Date(); startDate = new Date(); startDate.setFullYear(startDate.getFullYear() - 1);
  }
  return { startDate, endDate };
};

const TARGET_ATTACH = 35; // coaching target for retail attach %

const ProductData = ({ currency = '£', onOpenFinance }) => {
  const [dateFilter, setDateFilter] = useState('last12months');
  const initial = useMemo(() => computePresetRange('last12months'), []);
  const [dateStart, setDateStart] = useState(initial.startDate);
  const [dateEnd, setDateEnd] = useState(initial.endDate);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [report, setReport] = useState(null);

  // Fetch the real product report for the selected range.
  useEffect(() => {
    let alive = true;
    productService.getProductReport({ startDate: dateStart.toISOString(), endDate: dateEnd.toISOString() })
      .then((res) => {
        if (!alive) return;
        const data = res && res.data ? (res.data.responseData !== undefined ? res.data.responseData : res.data) : null;
        setReport(data || null);
      })
      .catch(() => { if (alive) setReport(null); });
    return () => { alive = false; };
  }, [dateStart, dateEnd]);

  const totals = (report && report.totals) || {};
  const channelSplit = (report && report.channelSplit) || { pos: {}, online: {} };
  const topProducts = (report && report.topProducts) || [];
  const leaderboard = (report && report.leaderboard) || [];
  const opps = (report && report.opportunities) || [];

  const attachRate = totals.attachRatePct || 0;
  const attachedRevenue = (channelSplit.pos && channelSplit.pos.revenue) || 0;
  const avgRetailPerVisit = totals.avgRetailPerVisit || 0;
  const appointments = totals.appointments || 0;
  const upliftMoney = Math.max(0, Math.round(((TARGET_ATTACH - attachRate) / 100) * appointments * avgRetailPerVisit));

  const dateLabel = dateFilter === null && dateStart && dateEnd
    ? `${dateStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} to ${dateEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
    : getDatePresetLabel(dateFilter);

  const leaderRows = leaderboard.map((s, i) => ({
    id: s.staffId, name: s.name, value: fmtMoney(s.revenue, currency), valueNum: s.revenue,
    sub: `${s.units} sold`,
    badge: i === 0 ? 'Top seller' : undefined,
  }));

  const topRows = topProducts.map((p) => ({ name: p.name, value: fmtMoney(p.revenue, currency), valueNum: p.revenue, sub: `${p.units} sold` }));

  const onlineRev = (channelSplit.online && channelSplit.online.revenue) || 0;
  const posRev = (channelSplit.pos && channelSplit.pos.revenue) || 0;
  const channelTotal = onlineRev + posRev;
  const pct = (n) => (channelTotal > 0 ? Math.round((n / channelTotal) * 100) : 0);
  const channelSegments = [
    { label: 'Online', value: onlineRev, color: '#007EA0', pct: pct(onlineRev), meta: fmtMoney(onlineRev, currency) },
    { label: 'In person', value: posRev, color: '#14b8a6', pct: pct(posRev), meta: fmtMoney(posRev, currency) },
  ];

  const opportunities = opps.map((o) => ({ id: o.key, icon: 'bulb', tone: 'teal', metric: o.title, advice: o.detail }));

  return (
    <div className="voucher-v2 cd-scope pv2-sales-page">
      <div className="v2-sales-filterbar">
        <div className="v2-sales-fb-group">
          <div className="v2-sales-fb-anchor">
            <button type="button" className="v2-sales-fb-btn" onClick={() => setDatePickerOpen((o) => !o)}>
              <span className="v2-sales-fb-label">Date range</span>
              <span className="v2-sales-fb-sep" />
              <span className="v2-sales-fb-val">{dateLabel}</span>
              <ChevronDownIcon />
            </button>
            <DateRangePicker
              isOpen={datePickerOpen}
              onClose={() => setDatePickerOpen(false)}
              startDate={dateStart}
              endDate={dateEnd}
              selectedPreset={dateFilter}
              onPresetChange={(p) => { setDateFilter(p); const r = computePresetRange(p); setDateStart(r.startDate); setDateEnd(r.endDate); }}
              onApply={(s, e) => { setDateStart(s); setDateEnd(e); setDateFilter(null); }}
            />
          </div>
        </div>
        {onOpenFinance && (
          <div className="v2-sales-fb-right">
            <button type="button" className="pv2-finance-link" onClick={onOpenFinance}>
              <span>Product transactions in Finance</span><ArrowRightIcon />
            </button>
          </div>
        )}
      </div>

      <div className="cd-stack">
        <HeroInsight
          eyebrow="Retail attach"
          headline={`Your team added ${fmtMoney(attachedRevenue, currency)} of retail on top of appointments`}
          sub={`${attachRate}% of in-person visits left with a product. Reaching ${TARGET_ATTACH}% could add about ${fmtMoney(upliftMoney, currency)} over this period.`}
          metric={{ value: attachedRevenue, format: (n) => fmtMoney(n, currency), caption: 'added this period' }}
          tone="teal"
        />

        <StatTiles tiles={[
          { label: 'Units sold', value: String(totals.unitsSold || 0), hint: 'products sold this period' },
          { label: 'Attach rate', value: `${attachRate}%`, hint: 'of visits include a product' },
          { label: 'Avg retail per visit', value: fmtMoney(avgRetailPerVisit, currency), hint: 'when a client buys' },
          { label: 'Retail margin', value: `${totals.marginPct || 0}%`, hint: 'after product cost' },
        ]} />

        <Leaderboard title="Retail champions" rows={leaderRows} />

        <div className="cd-grid-2">
          <BarList title="Top products" rows={topRows} />
          <SegmentSplit title="Online vs in person" segments={channelSegments} />
        </div>

        <OpportunityList title="Opportunities" items={opportunities} />
      </div>
    </div>
  );
};

export default ProductData;
